/**
 * Event Calendar View
 * Renders a monthly calendar grid with event indicators
 */

import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, isToday, getDay, addMonths, subMonths, parseISO } from "date-fns";
import { de } from "date-fns/locale/de";

document.addEventListener("turbo:load", function () {
  const calendarContainer = document.querySelector(".events-calendar");
  if (!calendarContainer) return;

  const eventsDataEl = calendarContainer.querySelector("#events-data");
  if (!eventsDataEl) return;

  let events = [];
  try {
    events = JSON.parse(eventsDataEl.textContent);
  } catch (e) {
    console.error("Failed to parse events data", e);
    return;
  }

  // Convert ISO date strings to Date objects
  events = events.map((event) => ({
    ...event,
    starts_on: parseISO(event.starts_on),
    ends_on: parseISO(event.ends_on),
  }));

  let currentDate = new Date();
  const monthYearEl = calendarContainer.querySelector(".calendar-month-year");
  const calendarDaysEl = document.getElementById("calendar-days");
  const prevBtn = calendarContainer.querySelector("[data-action='prev-month']");
  const nextBtn = calendarContainer.querySelector("[data-action='next-month']");
  const eventsModalEl = document.getElementById("calendar-events-modal");
  const selectedDateEl = document.getElementById("selected-date");
  const selectedDateEventsEl = document.getElementById("selected-date-events");

  // Initialize Bootstrap Modal
  let bootstrapModal = null;
  if (eventsModalEl && window.bootstrap && window.bootstrap.Modal) {
    bootstrapModal = new window.bootstrap.Modal(eventsModalEl);
  }

  // Initialize with current month
  if (monthYearEl.dataset.currentMonth && monthYearEl.dataset.currentYear) {
    currentDate = new Date(
      parseInt(monthYearEl.dataset.currentYear),
      parseInt(monthYearEl.dataset.currentMonth) - 1,
      1
    );
  }

  function escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  // Color palette for events - 15 distinct, high-contrast colors
  const eventColors = [
    { bg: '#e3f2fd', text: '#0d47a1', hover: '#0d47a1' }, // Blue
    { bg: '#f3e5f5', text: '#4a148c', hover: '#4a148c' }, // Purple
    { bg: '#e8f5e9', text: '#1b5e20', hover: '#1b5e20' }, // Green
    { bg: '#fff3e0', text: '#e65100', hover: '#e65100' }, // Orange
    { bg: '#fce4ec', text: '#880e4f', hover: '#880e4f' }, // Pink
    { bg: '#e0f2f1', text: '#004d40', hover: '#004d40' }, // Teal
    { bg: '#ede7f6', text: '#311b92', hover: '#311b92' }, // Deep Purple
    { bg: '#e1f5fe', text: '#01579b', hover: '#01579b' }, // Light Blue
    { bg: '#f1f8e9', text: '#33691e', hover: '#33691e' }, // Light Green
    { bg: '#fff9c4', text: '#f57f17', hover: '#f57f17' }, // Yellow
    { bg: '#ffccbc', text: '#bf360c', hover: '#bf360c' }, // Deep Orange
    { bg: '#d7ccc8', text: '#3e2723', hover: '#3e2723' }, // Brown
    { bg: '#b3e5fc', text: '#01579b', hover: '#01579b' }, // Cyan
    { bg: '#c5cae9', text: '#1a237e', hover: '#1a237e' }, // Indigo
    { bg: '#f8bbd0', text: '#880e4f', hover: '#880e4f' }, // Rose
  ];

  // Store color assignments globally based on event name for consistency
  const eventNameColorAssignments = new Map();
  // Store color assignments per day to ensure different colors on same day
  const dayColorAssignments = new Map();

  function getEventColor(eventId, dateStr, eventName) {
    // Create a unique key for this day
    const dayKey = dateStr;

    // Initialize color tracking for this day if not exists
    if (!dayColorAssignments.has(dayKey)) {
      dayColorAssignments.set(dayKey, new Map());
    }

    const dayColors = dayColorAssignments.get(dayKey);

    // If we already assigned a color to this event name globally, try to use it
    if (eventNameColorAssignments.has(eventName)) {
      const preferredColor = eventNameColorAssignments.get(eventName);
      const preferredIndex = eventColors.indexOf(preferredColor);

      // Check if this color is already used on this day
      const usedColors = new Set(Array.from(dayColors.values()).map(c => eventColors.indexOf(c)));

      if (!usedColors.has(preferredIndex)) {
        // Color is available, use it
        dayColors.set(eventId, preferredColor);
        return preferredColor;
      }
    }

    // Get colors already used on this day
    const usedColors = new Set(Array.from(dayColors.values()).map(c => eventColors.indexOf(c)));

    // Hash the event name (not ID) for consistent colors across same-named events
    let hash = 0;
    for (let i = 0; i < eventName.length; i++) {
      hash = ((hash << 5) - hash) + eventName.charCodeAt(i);
      hash = hash & hash; // Convert to 32bit integer
    }
    hash = ((hash >> 16) ^ hash) * 0x45d9f3b;
    hash = ((hash >> 16) ^ hash) * 0x45d9f3b;
    hash = (hash >> 16) ^ hash;

    // Start with the hash-based color
    let colorIndex = Math.abs(hash) % eventColors.length;

    // If this color is already used on this day, find the next available one
    let attempts = 0;
    while (usedColors.has(colorIndex) && attempts < eventColors.length) {
      colorIndex = (colorIndex + Math.abs(hash % 7) + 1) % eventColors.length;
      attempts++;
    }

    const selectedColor = eventColors[colorIndex];

    // Store this color assignment for this event name globally
    if (!eventNameColorAssignments.has(eventName)) {
      eventNameColorAssignments.set(eventName, selectedColor);
    }

    dayColors.set(eventId, selectedColor);

    return selectedColor;
  }

  function renderCalendar() {
    // Clear color assignments when rendering new calendar
    dayColorAssignments.clear();

    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);

    // Find first Monday of the month (or before if month doesn't start on Monday)
    const startDate = new Date(monthStart);
    const dayOfWeek = getDay(startDate); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    // Calculate days to subtract to get to Monday (1)
    // If Sunday (0), go back 6 days; if Monday (1), go back 0 days; etc.
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    startDate.setDate(startDate.getDate() - daysToSubtract);

    // Find last Friday of the month (or after if month doesn't end on Friday)
    const endDate = new Date(monthEnd);
    const endDayOfWeek = getDay(endDate);
    // Calculate days to add to get to Friday (5)
    // If Friday (5), add 0; if Saturday (6), add 6; if Sunday (0), add 5; etc.
    const daysToAdd = endDayOfWeek === 0 ? 5 : endDayOfWeek === 6 ? 6 : 5 - endDayOfWeek;
    endDate.setDate(endDate.getDate() + daysToAdd);

    const allDays = eachDayOfInterval({ start: startDate, end: endDate });

    // Filter out weekends (Saturday = 6, Sunday = 0)
    const days = allDays.filter((day) => {
      const dayOfWeek = getDay(day);
      return dayOfWeek >= 1 && dayOfWeek <= 5; // Monday (1) to Friday (5)
    });

    // Update month/year header
    monthYearEl.textContent = format(currentDate, "MMMM yyyy", { locale: de });
    monthYearEl.dataset.currentMonth = currentDate.getMonth() + 1;
    monthYearEl.dataset.currentYear = currentDate.getFullYear();

    // Render calendar days
    calendarDaysEl.innerHTML = days
      .map((day) => {
        const dayEvents = getEventsForDate(day, events);
        const isCurrentMonth = isSameMonth(day, currentDate);
        const isCurrentDay = isToday(day);
        const eventCount = dayEvents.length;
        const hasEvents = eventCount > 0;

        let eventNames = "";
        if (hasEvents) {
            // Show event names, truncate if too long
            const dateStr = format(day, "yyyy-MM-dd");
            eventNames = dayEvents
              .map((event) => {
                const fullName = `${event.topic} ${event.type_name}`;
                // Truncate to ~30 characters for better display
                const truncatedName = fullName.length > 30 ? fullName.substring(0, 27) + "..." : fullName;
                const color = getEventColor(event.id, dateStr, event.topic);
                return `<div class="calendar-event-name"
                             data-event-id="${event.id}"
                             style="background-color: ${color.bg}; color: ${color.text}; --hover-color: ${color.hover};"
                             title="${escapeHtml(fullName)}"
                             role="button"
                             tabindex="0">${escapeHtml(truncatedName)}</div>`;
              })
              .join("");
          }

        return `
          <div class="calendar-day ${!isCurrentMonth ? "calendar-day--other-month" : ""} ${isCurrentDay ? "calendar-day--today" : ""} ${hasEvents ? "calendar-day--has-events" : ""}"
               data-date="${format(day, "yyyy-MM-dd")}"
               ${hasEvents ? 'data-events-count="' + eventCount + '"' : ""}>
            <div class="calendar-day-number">${format(day, "d")}</div>
            <div class="calendar-day-events">${eventNames}</div>
          </div>
        `;
      })
      .join("");

    // Add click handlers to individual events
    calendarDaysEl.querySelectorAll(".calendar-event-name").forEach((eventEl) => {
      eventEl.addEventListener("click", function (e) {
        e.stopPropagation(); // Prevent day click handler from firing
        const eventId = parseInt(this.dataset.eventId);
        const event = events.find((e) => e.id === eventId);
        if (event) {
          showSingleEvent(event);
        }
      });

          // Also handle keyboard events for accessibility
          eventEl.addEventListener("keydown", function (e) {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              const eventId = parseInt(this.dataset.eventId);
              const event = events.find((e) => e.id === eventId);
              if (event) {
                showSingleEvent(event);
              }
            }
          });
        });

        // Remove day click handlers - only individual events can be clicked now
        // calendarDaysEl.querySelectorAll(".calendar-day--has-events").forEach((dayEl) => {
        //   dayEl.addEventListener("click", function (e) {
        //     // Only show all events if clicking on the day number or empty area, not on an event
        //     if (e.target.classList.contains("calendar-day-number") || e.target.classList.contains("calendar-day")) {
        //       const dateStr = this.dataset.date;
        //       const date = parseISO(dateStr);
        //       showEventsForDate(date, getEventsForDate(date, events));
        //     }
        //   });
        // });
  }

  function getEventsForDate(date, eventsList) {
    return eventsList.filter((event) => {
      const eventStart = event.starts_on;
      const eventEnd = event.ends_on;
      return (
        isSameDay(eventStart, date) ||
        isSameDay(eventEnd, date) ||
        (eventStart <= date && eventEnd >= date)
      );
    });
  }

  function formatEarlyBirdDate(daysLeft) {
    const today = new Date();
    const targetDate = new Date(today);
    targetDate.setDate(today.getDate() + daysLeft);
    return format(targetDate, "dd.MM.yyyy", { locale: de });
  }

  function showSingleEvent(event) {
    // For single event, show just the event name in header
    selectedDateEl.textContent = `${event.topic} ${event.type_name}`;
    selectedDateEventsEl.innerHTML = renderSingleEvent(event);
    showEventsModal();

    // Initialize tooltips for status indicator bars
    setTimeout(() => {
      if (window.bootstrap && window.bootstrap.Tooltip) {
        document.querySelectorAll('#calendar-events-modal [data-bs-toggle="tooltip"]').forEach(el => {
          const existingTooltip = window.bootstrap.Tooltip.getInstance(el);
          if (existingTooltip) {
            existingTooltip.dispose();
          }
          new window.bootstrap.Tooltip(el);
        });
      }
    }, 50);
  }

  function renderSingleEvent(event) {
    const detailsUrl = `/seminare-schulungen-kurse/${event.course_slug}?event_id=${event.slug}`;
    // const bookingUrl = `/seminare-schulungen-kurse/${event.course_slug}/${event.slug}/event_bookings/new`;

    // Format full date range
    const startDateFull = format(event.starts_on, "EEEE, d. MMMM yyyy", { locale: de });
    const endDateFull = format(event.ends_on, "EEEE, d. MMMM yyyy", { locale: de });
    const dateRangeFull = isSameDay(event.starts_on, event.ends_on)
      ? startDateFull
      : `${startDateFull} - ${endDateFull}`;

    // Status badge - only show if not bookable
    let statusBadge = "";
    if (!event.bookable || event.booking_spaces_remaining === 0) {
      statusBadge = '<div class="mb-3"><span class="badge bg-danger">Ausgebucht</span></div>';
    }

    // Early bird section
    let earlyBirdSection = "";
    if (event.super_early_bird_active && event.super_early_bird_days_left !== null) {
      const deadline = formatEarlyBirdDate(event.super_early_bird_days_left);
      earlyBirdSection = `
        <div class="p-3 bg-warning bg-opacity-10 border border-warning rounded-3 mb-3">
          <div class="d-flex align-items-center gap-2 mb-2">
            <span style="font-size: 1.2rem;">🔥</span>
            <span class="fw-semibold text-warning">Super Early Bird</span>
          </div>
          <div class="small">
            Sichere dir den <strong>zeitlich limitierten Rabatt</strong> - nur noch bis ${deadline}!
          </div>
        </div>
      `;
    } else if (event.early_bird_active && event.early_bird_days_left !== null) {
      const deadline = formatEarlyBirdDate(event.early_bird_days_left);
      earlyBirdSection = `
        <div class="p-3 bg-success bg-opacity-10 border border-success rounded-3 mb-3">
          <div class="d-flex align-items-center gap-2 mb-2">
            <span style="font-size: 1.2rem;">⏰</span>
            <span class="fw-semibold text-success">Early Bird</span>
          </div>
          <div class="small">
            Sichere dir den <strong>zeitlich limitierten Rabatt</strong> - nur noch bis ${deadline}!
          </div>
        </div>
      `;
    }

    // Location info
    const locationIcon = event.city === "Remote" ? "desktop" : "building";
    const locationText = event.city === "Remote" ? "Remote Workshop" : `${escapeHtml(event.location_name)}, ${escapeHtml(event.city)}`;
    const locationType = event.city === "Remote"
      ? '<span class="badge bg-light text-muted border small"><i class="far fa-desktop me-1"></i>Online Schulung</span>'
      : '<span class="badge bg-light text-dark border small"><i class="far fa-building me-1"></i>Vor Ort Schulung</span>';

    // Days and language info
    const daysText = event.days === 1 ? "1 Tag" : `${event.days} Tage`;
    const languageText = event.language === "de" ? "Deutsch" : event.language === "en" ? "Englisch" : event.language;

    return `
      <div class="event-modal-content">
        ${earlyBirdSection}
        ${statusBadge}

        <!-- Date & Time -->
        <div class="mb-3">
          <div class="d-flex align-items-start gap-3">
            <div class="flex-shrink-0 text-muted" style="width: 24px;">
              <i class="far fa-calendar"></i>
            </div>
            <div class="flex-grow-1">
              <div class="text-muted small">Termin</div>
              <div class="fw-medium">${dateRangeFull}</div>
            </div>
          </div>
        </div>

        <!-- Duration -->
        <div class="mb-3">
          <div class="d-flex align-items-start gap-3">
            <div class="flex-shrink-0 text-muted" style="width: 24px;">
              <i class="far fa-clock"></i>
            </div>
            <div class="flex-grow-1">
              <div class="text-muted small">Dauer</div>
              <div class="fw-medium">${daysText}</div>
            </div>
          </div>
        </div>

        <!-- Language -->
        <div class="mb-3">
          <div class="d-flex align-items-start gap-3">
            <div class="flex-shrink-0 text-muted" style="width: 24px;">
              <i class="far fa-language"></i>
            </div>
            <div class="flex-grow-1">
              <div class="text-muted small">Sprache</div>
              <div class="fw-medium">${languageText}</div>
            </div>
          </div>
        </div>

        <!-- Location -->
        <div class="mb-4">
          <div class="d-flex align-items-start gap-3">
            <div class="flex-shrink-0 text-muted" style="width: 24px;">
              <i class="far fa-${locationIcon}"></i>
            </div>
            <div class="flex-grow-1">
              <div class="text-muted small">Veranstaltungsort</div>
              <div class="fw-medium mb-2">${locationText}</div>
              ${locationType}
            </div>
          </div>
        </div>

        <!-- Action buttons -->
        <div class="d-flex flex-column flex-sm-row gap-2 pt-3 border-top">
          <a href="${detailsUrl}" class="btn btn-cta flex-grow-1 mouse-cursor-gradient-tracking py-2h px-4h">
            <span class="gradient-helper" aria-hidden="true"></span>
            <span class="btn-text">Mehr zum Kurs</span>
          </a>
        </div>
      </div>
    `;
  }

  function showEventsForDate(date, dateEvents) {
    if (dateEvents.length === 0) {
      hideEventsModal();
      return;
    }

    selectedDateEl.textContent = format(date, "EEEE, d. MMMM yyyy", { locale: de });

    // For multiple events on the same date, wrap each in a card with spacing
    selectedDateEventsEl.innerHTML = dateEvents
      .map((event) => {
        return `<div class="mb-3">${renderSingleEvent(event)}</div>`;
      })
      .join("");

    showEventsModal();

    // Initialize tooltips for status indicator bars
    setTimeout(() => {
      if (window.bootstrap && window.bootstrap.Tooltip) {
        document.querySelectorAll('#calendar-events-modal [data-bs-toggle="tooltip"]').forEach(el => {
          const existingTooltip = window.bootstrap.Tooltip.getInstance(el);
          if (existingTooltip) {
            existingTooltip.dispose();
          }
          new window.bootstrap.Tooltip(el);
        });
      }
    }, 50);
  }

  function showEventsModal() {
    if (bootstrapModal) {
      bootstrapModal.show();
    }
  }

  function hideEventsModal() {
    if (bootstrapModal) {
      bootstrapModal.hide();
    }
  }

  // Navigation handlers
  prevBtn.addEventListener("click", function () {
    currentDate = subMonths(currentDate, 1);
    renderCalendar();
    eventsDetailEl.style.display = "none";
  });

  nextBtn.addEventListener("click", function () {
    currentDate = addMonths(currentDate, 1);
    renderCalendar();
    eventsDetailEl.style.display = "none";
  });

  // Initial render
  renderCalendar();
});

